import React from 'react';
import { Box, Typography } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';

interface RequiredStepSectionHeaderProps {
  stepNumber: number;
  title: string;
  subtitle?: string;
  required?: boolean;
  isComplete?: boolean;
  hasError?: boolean;
}

const RequiredStepSectionHeader: React.FC<RequiredStepSectionHeaderProps> = ({
  stepNumber,
  title,
  subtitle,
  required,
  isComplete = false,
  hasError = false,
}) => {
  const circleSize = 28;

  const circleColor = hasError
    ? appColors.status.error
    : isComplete
      ? appColors.primary.main
      : appColors.primary.main;

  const circleBg = hasError
    ? appColors.status.error
    : isComplete
      ? appColors.primary.main
      : 'transparent';

  const circleTextColor = hasError || isComplete ? '#FFFFFF' : appColors.primary.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
      <Box
        aria-hidden="true"
        data-testid={`step-marker-${stepNumber}`}
        data-step-state={hasError ? 'error' : isComplete ? 'complete' : 'incomplete'}
        sx={{
          width: circleSize,
          height: circleSize,
          minWidth: circleSize,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${circleColor}`,
          bgcolor: circleBg,
          flexShrink: 0,
          mt: '2px',
          transition: 'background-color 0.2s, border-color 0.2s',
        }}
      >
        {isComplete && !hasError ? (
          <CheckIcon sx={{ fontSize: 16, color: circleTextColor }} />
        ) : (
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 800,
              lineHeight: 1,
              color: circleTextColor,
              userSelect: 'none',
            }}
          >
            {stepNumber}
          </Typography>
        )}
      </Box>
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, fontSize: '1.125rem', color: appColors.text.heading, lineHeight: 1.4 }}
        >
          {title}
          {required && (
            <Typography component="span" sx={{ color: appColors.status.error, ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: appColors.text.caption, lineHeight: 1.4 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RequiredStepSectionHeader;
